import { IContact } from "@/models/contacts/contact.types"
import {
  AddressDto,
  UpdateAccountContact,
} from "@/api/backofficeRest/generated/model"
import { IContactEditableFields } from "./ContactMutator.types"

export interface IContactCommsInput {
  receivePost: boolean
  receiveEmail: boolean
  receiveSMS: boolean
}

/*
  The update endpoint requires all three comms flags, but the read returns them
  absent when Junifer holds no value — absent means UNKNOWN. Sending false would
  silently opt the customer out of post, email or SMS, so refuse the save
  instead. Not seen on staging (0 of 40 sampled contacts), which is what makes
  refusing an acceptable trade.
*/
export const requireStoredComms = (contact: IContact): IContactCommsInput => {
  if (
    contact.receivePost === null ||
    contact.receiveEmail === null ||
    contact.receiveSMS === null
  ) {
    throw new Error(
      "This contact's communication preferences are missing in Junifer, so it cannot be saved from Nova.",
    )
  }

  return {
    receivePost: contact.receivePost,
    receiveEmail: contact.receiveEmail,
    receiveSMS: contact.receiveSMS,
  }
}

/*
  The update endpoint REQUIRES address at runtime, even though the schema marks
  it optional and the ticket said to omit it — verified against staging on
  2026-09-09, where omitting it fails the whole request with "The required
  parameter object 'address' is missing". Echoing the stored address back is
  what keeps a save from wiping it; all six AddressDto fields are selected by
  the read, so the echo is lossless.
*/
export const requireStoredAddress = (contact: IContact): AddressDto => {
  if (!contact.address) {
    throw new Error(
      "This contact has no address in Junifer, so it cannot be saved from Nova.",
    )
  }

  return {
    address1: contact.address.address1 ?? undefined,
    address2: contact.address.address2 ?? undefined,
    address3: contact.address.address3 ?? undefined,
    address4: contact.address.address4 ?? undefined,
    address5: contact.address.address5 ?? undefined,
    postcode: contact.address.postcode,
  }
}

/*
  Only keys the agent actually edited are emitted. An edited empty string IS a
  change (clearing a phone number) and must be sent; an absent key must not be,
  so the gateway copies Junifer's stored value forward.
*/
const editedFields = (edits: Partial<IContactEditableFields>) => ({
  ...(edits.title !== undefined && { title: edits.title }),
  ...(edits.forename !== undefined && { forename: edits.forename }),
  ...(edits.email !== undefined && { email: edits.email }),
  ...(edits.phone1 !== undefined && { phone1: edits.phone1 }),
  ...(edits.phone2 !== undefined && { phone2: edits.phone2 }),
})

/*
  Builds the full state the update endpoint requires: the surname and all three
  comms flags always, the agent's edits on top, and nothing else — `primary`,
  `dateOfBirth`, `address` and `fromDttm` are all optional, and omitting them
  preserves Junifer's stored values.
*/
export const buildUpdateContactInput = ({
  contact,
  edits = {},
  comms,
  extra = {},
}: {
  contact: IContact
  edits?: Partial<IContactEditableFields>
  /* Supplied when the caller is replacing the preferences outright. */
  comms?: IContactCommsInput
  extra?: Partial<UpdateAccountContact>
}): UpdateAccountContact => ({
  surname: edits.surname ?? contact.lastName,
  address: requireStoredAddress(contact),
  ...(comms ?? requireStoredComms(contact)),
  ...editedFields(edits),
  ...extra,
})
